import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import {
  applyGiftWrapCartSections,
  fetchCart,
  findCartItemByKey,
  findLineIndexByKey,
  getCartSectionIds,
  mergeGiftWrapProperty,
  syncGiftWrapFee,
} from '@theme/gift-wrap-pdp';

/**
 * Per-line gift wrap toggle for cart items (marks line property + syncs flat fee).
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} error
 *
 * @extends {Component<Refs>}
 */
class GiftWrapLineOption extends Component {
  #loading = false;

  /**
   * @param {Event} event
   */
  handleToggle(event) {
    if (!(event.target instanceof HTMLInputElement) || this.#loading) return;
    this.#setWrapMark(event.target.checked);
  }

  /**
   * @param {boolean} marked
   */
  async #setWrapMark(marked) {
    const cfg = Theme.giftWrap;
    if (!cfg?.enabled) return;

    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.#setLoading(true);
    this.#hideError();

    try {
      const cart = await fetchCart();
      const lineIndex = findLineIndexByKey(cart.items, lineKey);
      const item = findCartItemByKey(cart.items, lineKey);
      if (!lineIndex || !item) {
        if (this.refs.checkbox) this.refs.checkbox.checked = !marked;
        return;
      }

      const sections = getCartSectionIds();
      const changeUrl = Theme.routes.cart_change_url.endsWith('.js')
        ? Theme.routes.cart_change_url
        : `${Theme.routes.cart_change_url}.js`;

      const body = JSON.stringify({
        line: lineIndex,
        quantity: item.quantity,
        properties: mergeGiftWrapProperty(item.properties, marked),
        sections: sections.join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch(changeUrl, fetchConfig('json', { body }));
      const parsed = JSON.parse(await response.text());

      if (parsed.errors) {
        this.#showError(parsed.errors);
        if (this.refs.checkbox) this.refs.checkbox.checked = !marked;
        return;
      }

      if (parsed.sections) {
        applyGiftWrapCartSections(parsed, 'gift-wrap-line-option');
      }

      const feeResult = await syncGiftWrapFee({ sections });
      if (feeResult.changed && feeResult.parsed?.sections) {
        applyGiftWrapCartSections(feeResult.parsed, 'gift-wrap-fee-sync');
      }
    } catch (error) {
      console.error(error);
      this.#showError('Could not update gift wrapping. Please try again.');
      if (this.refs.checkbox) this.refs.checkbox.checked = !marked;
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.#loading = loading;
    this.classList.toggle('gift-wrap-line-option--loading', loading);
    if (this.refs.checkbox) {
      this.refs.checkbox.disabled = loading;
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error } = this.refs;
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.classList.remove('hidden');
  }

  #hideError() {
    const { error } = this.refs;
    if (!(error instanceof HTMLElement)) return;
    error.textContent = '';
    error.classList.add('hidden');
  }
}

if (!customElements.get('gift-wrap-line-option')) {
  customElements.define('gift-wrap-line-option', GiftWrapLineOption);
}
